import { useEffect, useRef } from 'react';
import { cssVars } from '@/lib/cssVars';
import { cx } from '@/lib/cx';
import styles from './Waveform.module.css';

export type WaveformMode = 'idle' | 'listening' | 'hearing' | 'checking' | 'lit';

/** Subscribes to an input level in [0, 1]; returns an unsubscribe function. */
export type LevelSource = (listener: (level: number) => void) => () => void;

const BAR_COUNT = 44;

/** A fixed, speech-like envelope: louder in the middle, uneven like syllables. */
const BARS = Array.from({ length: BAR_COUNT }, (_, i) => {
  const x = i / (BAR_COUNT - 1);
  const envelope = Math.sin(Math.PI * x) ** 0.8;
  const texture = 0.55 + 0.45 * Math.abs(Math.sin(i * 1.7) * Math.cos(i * 0.45));
  return Math.max(0.12, envelope * texture);
});

/**
 * Voice waveform: dim when idle (the app is talking), cyan while waiting, bright gold while the
 * child's voice is heard, a scanning sweep while the attempt is checked, green after a good one.
 * While listening or hearing it follows the live microphone level (`level`) or gently bounces. Level updates write a CSS variable
 * directly, so they never re-render React.
 */
export function Waveform({ mode, level }: { mode: WaveformMode; level?: LevelSource | undefined }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = ref.current;
    if (!level || !element) return undefined;
    let smoothed = 0;
    const unsubscribe = level((value) => {
      // Fast attack, slower release, like a VU meter.
      smoothed = value > smoothed ? value : smoothed * 0.85 + value * 0.15;
      element.style.setProperty('--level', smoothed.toFixed(3));
    });
    return () => {
      unsubscribe();
      element.style.removeProperty('--level');
    };
  }, [level]);

  return (
    <div ref={ref} className={cx(styles.wave, styles[mode], level && styles.live)} aria-hidden>
      {BARS.map((height, i) => (
        <span key={i} className={styles.bar} style={cssVars({ '--h': height, '--i': i })} />
      ))}
    </div>
  );
}
